
import React from 'react';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { useFormSyncToHash } from '@/hooks/useFormSyncToHash';

const formSchema = z.object({
  address_line1: z.string().min(1, {
    message: "Address line 1 is required.",
  }),
  address_line2: z.string().optional(),
  city: z.string().min(1, {
    message: "City is required.",
  }),
  county: z.string().min(1, {
    message: "County is required.",
  }),
  postal_code: z.string().min(1, {
    message: "Postal code is required.",
  }),
  country: z.string().min(1, {
    message: "Country is required.",
  }),
});

type FormValues = z.infer<typeof formSchema>;

interface BusinessAddressFormProps {
  formData: any;
  updateFormData: (data: Partial<any>) => void;
  onNext: () => void;
  onPrev: () => void;
}

const BusinessAddressForm = ({
  formData,
  updateFormData,
  onNext,
  onPrev
}: BusinessAddressFormProps) => {
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      address_line1: formData.address_line1 || '',
      address_line2: formData.address_line2 || '',
      city: formData.city || '',
      county: formData.county || '',
      postal_code: formData.postal_code || '',
      country: formData.country || 'United Kingdom',
    },
  });

  // Use the custom hook to sync form data to URL hash
  useFormSyncToHash(form.watch, formData, updateFormData);

  const onSubmit = (values: FormValues) => {
    // Convert postal code to uppercase before saving
    const formattedValues = {
      ...values,
      postal_code: values.postal_code.toUpperCase()
    };
    updateFormData(formattedValues);
    onNext();
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold mb-2">Business Address</h2>
        <p className="text-muted-foreground">
          Provide your business address so community members can find you and know where you operate.
        </p>
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          <div className="space-y-4">
            <FormField
              control={form.control}
              name="address_line1"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Address Line 1</FormLabel>
                  <FormControl>
                    <Input placeholder="Street address" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="address_line2"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Address Line 2 (Optional)</FormLabel>
                  <FormControl>
                    <Input placeholder="Apartment, suite, unit, etc." {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="city"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>City</FormLabel>
                    <FormControl>
                      <Input placeholder="City" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="county"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>County</FormLabel>
                    <FormControl>
                      <Input placeholder="County" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="postal_code"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Postal Code</FormLabel>
                    <FormControl>
                      <Input placeholder="Postal code" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="country"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Country</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </div>
          
          <div className="flex justify-between pt-4">
            <Button type="button" variant="outline" onClick={onPrev}>Previous</Button>
            <Button type="submit">Next</Button>
          </div>
        </form>
      </Form>
    </div>
  );
};

export default BusinessAddressForm;
